import { useState, useEffect, useRef, useCallback } from "react";
import { Plus } from "lucide-react";
import toast from "react-hot-toast";
import {
    getMobileNumbers,
    updateMobileNumber,
    createMobileNumber,
} from "@/services/masters";
import { useScreenPermission } from "@/hooks/useScreenPermission";
import MasterModal from "../MasterModal";
import AddMobileNumberForm, {
    AddMobileNumberHandle,
    MobileNumberFormData,
} from "./AddMobileNumbers";
import MasterTable, { Column } from "../MasterTable";
import MasterTableHeader from "../generics/MasterTableHeader";

interface MobileNumber {
    id: number;
    Country_Code: string;
    Number: string;
    Is_Active: boolean;
}

export interface TableColumn<T> {
    key: string;
    label: string;
    render?: (row: T) => React.ReactNode;
}

interface MobileNumbersResponse {
    results: MobileNumber[];
    total_pages: number;
    count: number;
}

export default function MobileNumbers() {
    const { canCreate, canUpdate } = useScreenPermission("mobile numbers");

    const [mobileNumbers, setMobileNumbers] = useState<MobileNumber[]>([]);
    const [editingItem, setEditingItem] = useState<MobileNumber | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const [togglingId, setTogglingId] = useState<string | number | null>(null);
    const [searchQuery, setSearchQuery] = useState("");
    const [statusFilter, setStatusFilter] = useState("");

    const formRef = useRef<AddMobileNumberHandle>(null);

    const loadMobileNumbers = useCallback(
  async (
    page: number,
    search: string = "",
    status: string = statusFilter
  ) => {
    setIsLoading(true);

    try {
      const { data, status: responseStatus } = await getMobileNumbers(
        page,
        search,
        status
      );

      if (responseStatus === 200 && data) {
        const isPaginated = !Array.isArray(data);

        const items: MobileNumber[] = isPaginated
          ? (data as MobileNumbersResponse).results
          : data;

        setMobileNumbers(items);

        if (isPaginated) {
          const paginated = data as MobileNumbersResponse;
          setTotalPages(paginated.total_pages ?? 1);
          setTotalCount(paginated.count ?? 0);
        } else {
          setTotalPages(1);
          setTotalCount(items.length);
        }
      }
    } catch {
      toast.error("Error loading mobile numbers");
    } finally {
      setIsLoading(false);
    }
  },
  [statusFilter]
);

    useEffect(() => {
        const delay = searchQuery ? 400 : 0;
        const timer = setTimeout(() => {
            setPage(1);
            loadMobileNumbers(1, searchQuery, statusFilter);
        }, delay);
        return () => clearTimeout(timer);
    }, [searchQuery, statusFilter, loadMobileNumbers]);


    const handleOpenModal = (item?: MobileNumber) => {
        setEditingItem(item || null);
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setEditingItem(null);

        setTimeout(() => {
            formRef.current?.reset?.();
        }, 0);
    };

    const handleSubmit = async (data: MobileNumberFormData) => {
        // formRef.current?.clearFieldErrors();
        setIsLoading(true);

        try {
            let response;
            const payload = {
                Is_Active: true,
                ...data,
            };
            if (editingItem) {
                response = await updateMobileNumber(editingItem.id, payload);
            } else {
                response = await createMobileNumber(payload);
            }
            if (response.status === 200 || response.status === 201) {
                toast.success(
                    editingItem
                        ? "Status updated successfully"
                        : "Mobile number added successfully"
                );

                handleCloseModal();
                await loadMobileNumbers(page, searchQuery, statusFilter);
            } else {

                const errorMessage =
                    response?.errors?.non_field_errors?.[0] ||
                    response?.message ||
                    response?.message ||
                    "Failed to save mobile number";
                toast.error(errorMessage);
            }
        } catch (err: any) {
            console.error(err);

            const errorMessage =
                err?.response?.errors?.non_field_errors?.[0] ||
                err?.response?.message ||
                err?.message ||
                "Failed to save mobile number";
            toast.error(errorMessage);
        } finally {
            setIsLoading(false);
        }
    };

    const handleToggleActive = useCallback(
  async (item: MobileNumber) => {
    if (!item.id) return;

    setTogglingId(item.id);

    try {
      const response = await updateMobileNumber(item.id, {
        Is_Active: !item.Is_Active,
      });

      if (response.status === 200) {
        toast.success(
          `Mobile number ${
            !item.Is_Active ? "activated" : "deactivated"
          } successfully`
        );

        await loadMobileNumbers(
          page,
          searchQuery,
          statusFilter
        );
      } else {
        toast.error("Failed to update mobile number status");
      }
    } catch {
      toast.error("Error updating mobile number status");
    } finally {
      setTogglingId(null);
    }
  },
  [loadMobileNumbers, page, searchQuery, statusFilter]
);

    const handleModalSubmit = () => {
        formRef.current?.submit();
    };

    const handleAdd = () => handleOpenModal();
    const handleEdit = (item: MobileNumber) => handleOpenModal(item);

const columns: Column<MobileNumber>[] = [
        {
            key: "Country_Code",
            label: "Country Code",
            render: (row) => row?.Country_Code ?? "-",
        },
        {
            key: "Number",
            label: "Number",
            render: (row) => row?.Number ?? "-",
        },
    ];

    return (
        <div className="space-y-6">
            <MasterTableHeader
                title="Mobile Numbers Management"
                description="Create, edit, and manage mobile numbers"
                canCreate={canCreate}
                addButtonText="Add New Mobile Number"
                onAdd={() => handleOpenModal()}
                // searchPlaceholder="Search by mobile number..."
                // searchQuery={searchQuery}
                // setSearchQuery={setSearchQuery}
                showDropdownFilter
                dropdownValue={statusFilter}
                onDropdownChange={(value) => {
                    setStatusFilter(value);
                    setPage(1);
                }}
                showClearFilters
                onClearFilters={() => {
                    setSearchQuery("");
                    setStatusFilter("");
                    setPage(1);

                    loadMobileNumbers(1, "", "");
                }}
            />
            <MasterTable
                columns={columns}
                data={mobileNumbers}
                onToggleActive={handleToggleActive}
                togglingId={togglingId}
                isLoading={isLoading && mobileNumbers.length === 0}
                currentPage={page}
                totalPages={totalPages}
                onPageChange={(p) => { setPage(p); loadMobileNumbers(p, searchQuery, statusFilter); }}
                canEdit={canUpdate}
                canToggle={canUpdate}
                onEdit={canUpdate ? handleEdit : undefined}
                totalCount={totalCount}
            />

            <MasterModal
                isOpen={isModalOpen}
                title={editingItem ? "Update Status" : "Add Mobile Number"}
                onClose={handleCloseModal}
                onSubmit={handleModalSubmit}
                isLoading={isLoading}
            >
                <AddMobileNumberForm
                    ref={formRef}
                    onSubmit={handleSubmit}
                    defaultValues={
                        editingItem
                            ? {
                                Country_Code: editingItem.Country_Code,
                                Number: editingItem.Number,
                            }
                            : undefined
                    }
                    isLoading={isLoading}
                />
            </MasterModal>
        </div>
    );
}